/**
 * a tool page is a prebuilt file, so it cannot hand the sign-in screen a return address the way an
 * in-app link does. it leaves the address here instead.
 *
 * only `/apps/<slug>` is ever accepted. a general "send me anywhere after sign-in" would let any page
 * choose where somebody lands the moment after they type their password.
 */

const STORAGE_KEY = 'weave-standalone-tool-return';

/** `/apps/<slug>` and nothing else — no query, no fragment, no other path in the app. */
const STANDALONE_TOOL_PATH = /^\/apps\/[a-z0-9-]+$/;

/**
 * somebody can leave for sign-in, give up, and sign in from the app later in the same tab — without
 * a deadline that abandoned note sends them to the tool page instead of where they were going.
 */
const NOTE_GOOD_FOR_MS = 10 * 60 * 1000;

const isStandaloneToolPath = (path: string): boolean => STANDALONE_TOOL_PATH.test(path);

interface StoredReturn {
  readonly path: string;
  readonly at: number;
}

/** storage throws in some privacy modes, and a lost return address is not worth failing sign-in. */
export const rememberStandaloneToolReturn = (path: string): void => {
  if (!isStandaloneToolPath(path)) return;
  try {
    const note: StoredReturn = { path, at: Date.now() };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(note));
  } catch {
    // no return address, so they land in the app after signing in
  }
};

/** reads without clearing, so it is safe to call while rendering. */
export const peekStandaloneToolReturn = (): string | null => {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (!stored) return null;
    const note = JSON.parse(stored) as Partial<StoredReturn>;
    if (typeof note?.path !== 'string' || typeof note?.at !== 'number') return null;
    if (Date.now() - note.at > NOTE_GOOD_FOR_MS) return null;
    return isStandaloneToolPath(note.path) ? note.path : null;
  } catch {
    return null;
  }
};

const clearStandaloneToolReturn = (): void => {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // a stale note is only ever used once, and only for an /apps address
  }
};

/** tolerant of a query, because sign-in appends things like `?welcome=new_user` before navigating. */
const isStandaloneToolDestination = (route: string): boolean => isStandaloneToolPath(route.split(/[?#]/)[0] ?? '');

/**
 * leaving is not instant, and the sign-in screen mounts again while the browser works on it — that
 * fresh copy would pick a second destination and race the first.
 */
let isLeavingTheApp = false;

/**
 * a tool page needs a full page load, or the router's catch-all lands somebody on the dashboard.
 * false keeps a failure here from costing a sign-in — the caller then navigates as it always did.
 */
export const tryReturnToToolPage = (route: string): boolean => {
  try {
    // used up the moment somebody is sent anywhere — dropping it earlier loses the destination when
    // a sign-in does not finish
    clearStandaloneToolReturn();
    if (!isStandaloneToolDestination(route)) return false;
    // the second caller of one sign-in, and the browser is already leaving
    if (isLeavingTheApp) return true;
    isLeavingTheApp = true;
    // replace, so the sign-in screen is not left behind for the back button
    window.location.replace(route);
    return true;
  } catch {
    return false;
  }
};
